import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { CachePatch } from '@/cache/patches';
import { createRealtimeCachePatchSink } from '@/realtime';

/**
 * The cross-window cache contract (ADR 0020 §3.3).
 *
 * There is no SSE yet, so two of our windows cannot learn of each other's
 * mutations through the server. They do not need to: the patch layer already
 * describes *what changed in the world* rather than *which caches to poke*, so
 * every window broadcasts the `CachePatch[]` it applies and every window
 * applies what it receives through `createRealtimeCachePatchSink`. A flag
 * changed in a popup and one changed in the main window therefore converge on
 * byte-identical cache state, and when SSE lands the same channel carries the
 * same values — nothing here is thrown away.
 *
 * ## How the outbound half is wired
 *
 * The patches are not recomputed here. Every optimistic mutation already stores
 * the exact patch set it applied in its mutation context
 * (`OptimisticContext.patches`, `hooks/useMessageMutations.ts`), so this module
 * subscribes to the `MutationCache` and forwards that array verbatim. Inventing
 * a second patch path — "broadcast something shaped like what the hook did" —
 * is exactly how two windows end up disagreeing.
 *
 * ## What it deliberately does not do
 *
 * A rollback is not broadcast: the patches are published when they are applied
 * optimistically, and if the server then refuses, the originating window
 * restores its snapshot while the other window keeps the optimistic state until
 * something refetches. Carrying rollbacks needs an inverse patch, which the
 * patch set does not define; ADR 0020 does not specify one either. Noted rather
 * than faked.
 */

export const CACHE_CHANNEL_NAME = 'ols-mail-cache';

export interface CachePatchMessage {
  type: 'cache-patches';
  patches: CachePatch[];
}

const PATCH_KINDS = new Set([
  'message-flags',
  'message-labels',
  'messages-moved',
  'messages-removed',
  'folder-counts',
  'folder-counts-set',
]);

function isCachePatch(value: unknown): value is CachePatch {
  if (typeof value !== 'object' || value === null) return false;
  const patch = value as { kind?: unknown; accountId?: unknown };
  return typeof patch.kind === 'string' && PATCH_KINDS.has(patch.kind) && typeof patch.accountId === 'string';
}

/**
 * Structural validation of everything that comes off the channel.
 *
 * Same-origin or not, this is data arriving from another document: it is
 * validated before it is allowed to touch the cache, never trusted by shape.
 */
export function isCachePatchMessage(value: unknown): value is CachePatchMessage {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as { type?: unknown; patches?: unknown };
  if (message.type !== 'cache-patches') return false;
  return Array.isArray(message.patches) && message.patches.every(isCachePatch);
}

/** The patch array an optimistic mutation stored in its context, if any. */
function patchesOf(context: unknown): CachePatch[] | undefined {
  if (typeof context !== 'object' || context === null) return undefined;
  const patches = (context as { patches?: unknown }).patches;
  if (!Array.isArray(patches) || patches.length === 0) return undefined;
  return patches.every(isCachePatch) ? (patches as CachePatch[]) : undefined;
}

/**
 * Wires this window into the channel for as long as it is mounted.
 *
 * Inbound: validated patches are applied through the realtime sink — the same
 * sink an event stream will write through.
 * Outbound: each optimistic mutation's patch set is published once, when the
 * mutation first carries it.
 */
export function useCachePatchChannel(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(CACHE_CHANNEL_NAME);
    const apply = createRealtimeCachePatchSink(queryClient);

    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (!isCachePatchMessage(event.data)) return;
      apply(event.data.patches);
    };

    const published = new Set<number>();
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const mutation = event.mutation;
      if (!mutation || published.has(mutation.mutationId)) return;
      const patches = patchesOf(mutation.state.context);
      if (!patches) return;
      published.add(mutation.mutationId);
      const message: CachePatchMessage = { type: 'cache-patches', patches };
      try {
        channel.postMessage(message);
      } catch {
        // A patch that cannot be structured-cloned is a programming error in
        // the patch shape, not something to crash the mutation over.
      }
    });

    return () => {
      unsubscribe();
      channel.close();
    };
  }, [queryClient]);
}

/**
 * The non-React half, for tests and for any future non-component caller
 * (an SSE connection relaying patches to windows without their own stream).
 */
export function publishCachePatches(patches: readonly CachePatch[]): void {
  if (typeof BroadcastChannel === 'undefined' || patches.length === 0) return;
  const channel = new BroadcastChannel(CACHE_CHANNEL_NAME);
  const message: CachePatchMessage = { type: 'cache-patches', patches: [...patches] };
  channel.postMessage(message);
  channel.close();
}

/** Applies inbound patches to a client without React. Exported for tests. */
export function subscribeCachePatches(client: QueryClient): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  const channel = new BroadcastChannel(CACHE_CHANNEL_NAME);
  const apply = createRealtimeCachePatchSink(client);
  channel.onmessage = (event: MessageEvent<unknown>) => {
    if (isCachePatchMessage(event.data)) apply(event.data.patches);
  };
  return () => channel.close();
}
